import { IANAZone } from 'luxon'
import * as z from 'zod/mini'
import { keyedIssue, refineWithKey } from '../errors/validation-keys'

/**
 * An e-mail address: trimmed, lowercased, then validated - the order matters, as `z.email()` alone rejects the
 * surrounding spaces a pasted address often has. Every schema that takes an address uses this, so a handler always
 * receives the stored form (`normalizeEmail`, docs/clients.md).
 */
export const emailField = z.string().check(z.trim(), z.toLowerCase(), z.email())

/**
 * A message the premise writes to a client, which rides out in an e-mail ("Správa od prevádzky"). Plain text, trimmed;
 * the e-mail escapes it and keeps its line breaks.
 */
export const messageField = z.string().check(z.trim(), z.maxLength(1000))

/**
 * A note the client writes for the premise in a public form ("Poznámka pre prevádzku"), where the event or the service
 * allows one. Plain text, trimmed; stored with the booking's snapshot and escaped wherever it is shown.
 */
export const customerNoteField = z.string().check(z.trim(), z.maxLength(1000))

/**
 * An array arriving in a query string. `toQueryParams` on the frontend sends an array as repeated
 * keys, which Express parses to a string for one value and an array for several - so both shapes
 * must be accepted. A comma-separated string is accepted too, as hand-written URLs use it.
 *
 * Takes the array schema rather than the item schema so the caller keeps `.min()`/`.max()`.
 * Built from a union rather than `z.preprocess`, so the inferred input stays `string | string[]`
 * and the output (`string[]`) is itself a valid input - see docs/validation.md.
 */
export const queryArray = <T extends z.ZodMiniType<unknown, string[]>>(array: T) =>
  z.pipe(
    z.union([
      z.pipe(
        z.string(),
        z.transform((value) => value.split(',')),
      ),
      z.array(z.string()),
    ]),
    array,
  )

/**
 * `end` must be strictly after `start`, reported on the end field. Works on ISO strings (HTTP
 * schemas) and `Date`s (operation inputs) alike. Skipped while either side is missing - whether a
 * bound is required is the field's own rule. A zero-length slot is rejected: it would occupy no time
 * and never show up in an overlap check.
 */
export const endAfterStart =
  <K extends string, L extends string>(startKey: K, endKey: L) =>
  <T extends { [P in K | L]?: string | Date | null }>(value: T, ctx: z.core.$RefinementCtx<T>) => {
    const start = value[startKey]
    const end = value[endKey]
    if (start == null || end == null) return
    if (new Date(end).getTime() <= new Date(start).getTime()) {
      ctx.addIssue(keyedIssue('validation.endAfterStart', [endKey]))
    }
  }

/**
 * A boolean arriving in a query string, where Express leaves it the string `'true'` or `'false'`.
 * A real boolean is accepted too, so the parsed output is itself a valid input (rule 5 in
 * docs/validation.md).
 */
export const queryBoolean = () =>
  z.pipe(
    z.union([z.boolean(), z.enum(['true', 'false'])]),
    z.transform((value) => value === true || value === 'true'),
  )

/**
 * A number arriving in a query string. Only a plain decimal is accepted (`'10'`, `'-2'`, `'1.5'`),
 * never `''` or `'1e3'`, which `Number()` would have let through. Takes the number schema, like
 * `queryArray`, so the caller keeps `z.int()` and its bounds.
 */
export const queryNumber = <T extends z.ZodMiniType<unknown, number>>(number: T) =>
  z.pipe(
    z.union([
      z.number(),
      z.pipe(
        z.string().check(z.regex(/^-?\d+(\.\d+)?$/)),
        z.transform((value) => Number(value)),
      ),
    ]),
    number,
  )

/** An instant on the wire: ISO 8601 with a `Z` or an offset, as `Date.prototype.toISOString` sends. */
export const isoDateTime = z.iso.datetime({ offset: true })

/** A calendar date with no time (`2026-09-18`). */
export const isoDate = z.iso.date()

/**
 * An ISO date **or** date-time. For the fields the old `@IsDateString` accepted in either form where
 * the senders really do send both. Prefer `isoDateTime` or `isoDate` whenever one form is enough.
 */
export const isoDateOrDateTime = z.union([isoDateTime, isoDate])

/** A wall-clock time of day, `HH:mm` on the 24-hour clock. Replaces `@IsMilitaryTime`. */
export const timeOfDay = z
  .string()
  .check(refineWithKey('validation.timeFormat', (value: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(value)))

/** An IANA timezone name (`Europe/Bratislava`), as stored in `EntityEntity.timezone`. */
export const ianaTimeZone = z
  .string()
  .check(refineWithKey('validation.timezone', (value: string) => IANAZone.isValidZone(value)))

/** `true` when `value` is a UUID string. For the few call sites that check an id outside a schema. */
export const isUuid = (value: unknown): value is string => z.uuid().safeParse(value).success
